import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction
} from '@reduxjs/toolkit'
import api from 'app/services/api'
import type { RootState } from 'app/store'
import { PAGE_SIZE } from 'config'
import { Product as ProductResult } from '.codegen/generatedApi'
import { ParsedProduct, parseProductResult, parseProductsQuery, selectQueryTotal } from 'helpers'
import type { Product, ResultsSliceState } from 'types'
import { selectMainCategoryDefaultImage } from 'app/selectors'

export const resultsAdapter = createEntityAdapter<ParsedProduct>({
  selectId: (item) => item.slug,
})

const initialState: ResultsSliceState = {
  perPage: PAGE_SIZE,
  page: 1,
  count: 0,
  status: 'idle',
}

export const resultsSlice = createSlice({
  name: 'results',
  initialState: resultsAdapter.getInitialState(initialState),
  reducers: {
    pageChanged: (state, { payload }: PayloadAction<number>) => {
      state.prevPage = state.page
      state.page = payload
    },
    resultsReceived: resultsAdapter.addMany,
    newSearchInit: (state) => {
      resultsAdapter.removeAll(state)
      state.count = 0
    },
    queryUpdated: (state, { payload }: PayloadAction<number>) => {
      state.page = 1
      state.count = payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(api.endpoints.searchProducts.matchPending, (state) => {
        state.status = 'loading'
      })
      .addMatcher(api.endpoints.searchProducts.matchRejected, (state) => {
        state.status = 'idle'
      })
      .addMatcher(api.endpoints.searchProducts.matchFulfilled, (state, { payload }) => {
        const products = parseProductsQuery(payload)

        const count = selectQueryTotal(payload) ?? 0
        resultsAdapter.addMany(state, products)
        state.count = count
        state.status = 'idle'
      })

    builder
      .addMatcher(api.endpoints.getProduct.matchPending, (state) => {
        state.status = 'loading'
      })
      .addMatcher(api.endpoints.getProduct.matchRejected, (state) => {
        state.status = 'idle'
      })
      .addMatcher(api.endpoints.getProduct.matchFulfilled, (state, { payload }) => {
        const product = payload.productBy ? parseProductResult(payload.productBy as ProductResult) : null

        if (product) {
          resultsAdapter.removeOne(state, product.slug)
          resultsAdapter.addOne(state, { ...product, status: 'full' })
        }
        state.status = 'idle'
      })
  },
})

export const { resultsReceived, newSearchInit } = resultsSlice.actions

const resultsStateSelector = (state: RootState) => state[resultsSlice.name]
const globalizedSelectors = resultsAdapter.getSelectors((state: RootState) => state[resultsSlice.name])

export const selectAllEntities = globalizedSelectors.selectAll

export const selectResultsState = createSelector(resultsStateSelector, (state) => state);

export const selectTotalCount = createSelector(resultsStateSelector, (state) => state.count)

export const selectResultsIsLoading = createSelector(resultsStateSelector, (state) => state.status === 'loading')

export const selectPagination = createSelector(resultsStateSelector, (state) => {
  const { count, page, prevPage = 1, perPage } = state
  const size = perPage || 1
  const offset = (page - 1) * size
  const totalPages = count ? Math.ceil(count / size) : 1

  return {
    page,
    prevPage,
    size,
    offset,
    totalPages,
  }
})

export const selectProductsBySlug =
  (slugs: string[]) =>
  (state: RootState): Product[] => {
    return slugs.reduce((products, slug) => {
      const maybeProduct = globalizedSelectors.selectById(state, slug)

      return maybeProduct ? products.concat(maybeProduct) : products
    }, [] as Product[])
  }

export const selectProductBySlug =
  (slug: string) =>
  (state: RootState): Product | undefined => {
    // console.log('Selected', slug)

    const maybeProduct = globalizedSelectors.selectById(state, slug)

    if (!maybeProduct) return undefined

    const product: Product = { ...maybeProduct }

    const { mainCategory, featuredImage } = product

    if (!featuredImage) {
      product.featuredImage = mainCategory?.id ? selectMainCategoryDefaultImage(mainCategory.id)(state) : undefined
    }

    return product
  }

export const { pageChanged, queryUpdated } = resultsSlice.actions

export default resultsSlice
